import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { SalesPeriodSelector } from "./SalesPeriodSelector";
import { SalesDashboardValueBoxes } from "./SalesDashboardValueBoxes";
import { DailySalesChart } from "./DailySalesChart";
import { SalesBreakdownPieChart } from "./SalesBreakdownPieChart";
import { SalesCurrencyPieChart } from "./SalesCurrencyPieChart";
import { SalesReceiptTypePieChart } from "./SalesReceiptTypePieChart";
import type { SalesPeriodState } from "../../hooks/useSalesPeriodState";
import { SectionTitle } from "../../components/SectionTitle";
import { spacing } from "@styling/sizing";

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  flex-wrap: wrap;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.x2}px;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 320px;
  }
`;

const PieChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.x2}px;

  @media (min-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

interface SalesStatsSectionProps {
  periodState: SalesPeriodState;
  resellerId?: string;
}

export const SalesStatsSection = ({
  periodState,
  resellerId,
}: SalesStatsSectionProps) => {
  const { t } = useTranslation();
  const {
    period,
    periodPreset,
    periodLabel,
    selectedDate,
    selectedDateLabel,
    canGoForward,
    handlePeriodChange,
    handlePresetChange,
    handlePrevPeriod,
    handleNextPeriod,
    handleSelectDate,
  } = periodState;

  const valueBoxProps = {
    resellerId,
    from: period.from,
    to: period.to,
    periodLabel,
    date: selectedDate,
    selectedDateLabel,
  };

  return (
    <>
      <SectionHeader>
          <SectionTitle>{t("adminDailySales.sectionTitle")}</SectionTitle>
          <SalesPeriodSelector
            period={period}
            periodPreset={periodPreset}
            periodLabel={periodLabel}
            canGoForward={canGoForward}
            onPrevious={handlePrevPeriod}
            onNext={handleNextPeriod}
            onPeriodChange={handlePeriodChange}
            onPresetChange={handlePresetChange}
          />
      </SectionHeader>
      <SalesDashboardValueBoxes {...valueBoxProps} />
      <ChartsRow>
        <DailySalesChart
          resellerId={resellerId}
          from={period.from}
          to={period.to}
          selectedDate={selectedDate}
          onSelectDate={handleSelectDate}
        />
        <SalesBreakdownPieChart
          resellerId={resellerId}
          date={selectedDate}
          selectedDateLabel={selectedDateLabel}
        />
      </ChartsRow>
      <PieChartsRow>
        {!resellerId && (
          <SalesCurrencyPieChart
            resellerId={resellerId}
            date={selectedDate}
            selectedDateLabel={selectedDateLabel}
          />
        )}
        <SalesReceiptTypePieChart
          resellerId={resellerId}
          date={selectedDate}
          selectedDateLabel={selectedDateLabel}
        />
      </PieChartsRow>
    </>
  );
};
